import { useEffect, useState, useCallback, ComponentType, useRef } from 'react'
import { Location, Action } from 'history'
import lodashLast from 'lodash/last'
import difference from 'lodash/difference'
import { BroadcastChannel } from 'broadcast-channel'
import { SingleUseLinkData } from '@chilipiper/api-type-def/src/bookingLinks'
import { parseQuery } from '@chilipiper/utils'
import { routesArray } from '../helpers'

declare global {
  interface Document {
    msHidden?: any
    webkitHidden?: any
  }
}

export interface RouterRoute {
  component: ComponentType<any>
  path: string
}

export const useRouterWillLeave = (
  processRedirect: string,
  closeRedirect: string | undefined,
  autoDial: boolean,
  setComponent: (component: string) => void,
  defaultLocation: Location
) => {
  const [location, setLocation] = useState<Location>(defaultLocation)
  const routerWillLeave = useCallback(
    (nextLocation: Location, action: Action) => {
      const { pathname } = nextLocation ?? {}
      const last = lodashLast(pathname?.split('/')) as string
      if (action === 'POP' && autoDial) {
        if (window.self !== window.top) {
          if (processRedirect || closeRedirect) {
            window.parent.postMessage(
              {
                message: 'CP_BACK_PRESSED',
                action: 'REDIRECT',
                url: processRedirect || closeRedirect,
              },
              '*'
            )
          } else {
            window.parent.postMessage(
              {
                message: 'CP_BACK_PRESSED',
                action: 'CLOSE_IFRAME',
              },
              '*'
            )
          }
        } else if (processRedirect || closeRedirect) {
          window.location.href = processRedirect || (closeRedirect as string)
        } else {
          window.location.href = document.referrer
        }
      } else {
        setLocation(nextLocation)
        setComponent(last)
      }
    },
    [autoDial, closeRedirect, processRedirect, setComponent]
  )
  return { location, routerWillLeave }
}

export const useHandleVisibilityChange = (createNewSession: () => void) => {
  const [hidden, setHidden] = useState<'hidden' | 'msHidden' | 'webkitHidden' | undefined>()
  const [visibilityChange, setVisibilityChange] = useState('')
  useEffect(() => {
    if (typeof document.hidden !== 'undefined') {
      setHidden('hidden')
      setVisibilityChange('visibilitychange')
    } else if (typeof document.msHidden !== 'undefined') {
      setHidden('msHidden')
      setVisibilityChange('msvisibilitychange')
    } else if (typeof document.webkitHidden !== 'undefined') {
      setHidden('webkitHidden')
      setVisibilityChange('webkitvisibilitychange')
    }
  }, [])
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (hidden && !document[hidden]) {
        createNewSession()
      }
    }
    if (hidden && visibilityChange) {
      document.addEventListener(visibilityChange, handleVisibilityChange, false)
      return () => document.removeEventListener(visibilityChange, handleVisibilityChange)
    }
  }, [hidden, visibilityChange])
}

export const useNavigation = (
  splat: string,
  isIntermediate: boolean,
  singleUseLinkData?: SingleUseLinkData
) => {
  const [component, setComponent] = useState('calendar')
  const [view, setView] = useState('')
  const [token, setToken] = useState('')
  const [queue, setQueue] = useState('')
  const [assignee, setAssignee] = useState('')
  const [uri, setUri] = useState('')
  const [search, setSearch] = useState('')
  const splitInitialSplat = useRef(splat.split('/'))
  const { token: tokenParam } = parseQuery()

  useEffect(() => {
    const diff = difference(splitInitialSplat.current, routesArray)
    const last = splitInitialSplat.current[splitInitialSplat.current.length - 1]

    setQueue(singleUseLinkData?.queue || diff[0])
    setAssignee(diff[0] === 'once' ? (singleUseLinkData?.assignee ?? '') : diff[1])
    setUri(`/book/${diff.join('/')}`)

    setComponent(isIntermediate ? 'intermidiate' : last || 'calendar')
    setView(last || '')

    const templateId = singleUseLinkData?.templateId
    if (templateId) {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('type', templateId)
      setSearch(`?${searchParams.toString()}`)
    } else {
      setSearch(window.location.search)
    }
  }, [isIntermediate, singleUseLinkData, setAssignee, setComponent, setView])
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape' || event.keyCode === 27) {
        window.parent?.postMessage({ action: 'esc', args: [] }, '*')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
  useEffect(() => {
    if (token) {
      const onBroadcast = (e: any) => {
        if (e.newTabOpened) {
          setComponent('another-tab')
        }
      }
      const channel = new BroadcastChannel(token)
      channel.onmessage = onBroadcast
      channel.postMessage({ newTabOpened: true })
    }
  }, [token])
  useEffect(() => {
    if (tokenParam) {
      setToken(tokenParam)
    }
  }, [tokenParam])
  return {
    search,
    uri,
    assignee,
    queue,
    view,
    component,
    setComponent,
  }
}
